/**
 * Logging types for structured log entries.
 */

import type { JsonArray, JsonObject, JsonValue } from './common.types';
import type { OpaqueCursorPagination } from './authorization.types';
import { IPG_FORBIDDEN_LOG_DATA_KEYS } from './types/ipg/ipg-logging-redaction-registry';

export type LogSeverity =
    | 'DEFAULT'
    | 'DEBUG'
    | 'INFO'
    | 'NOTICE'
    | 'WARNING'
    | 'ERROR'
    | 'CRITICAL'
    | 'ALERT'
    | 'EMERGENCY';

export type LogService =
    | 'pillar-backend'
    | 'pillar-frontend'
    | 'samba-core'
    | 'samba-preload'
    | 'samba-website'
    | 'samba-sidebar';

export type LogTopic = string;

export type SambaImageTag = string;

export interface LogException extends JsonObject {
    name: string;
    message: string;
    stack?: string;
    cause?: LogException;
}

export const FORBIDDEN_LOG_DATA_KEYS = [
    'password',
    'old_password',
    'new_password',
    'passphrase',
    'secret',
    'client_secret',
    'api_key',
    'apiKey',
    'authorization',
    'cookie',
    'set-cookie',
    'token',
    'access_token',
    'refresh_token',
    'id_token',
    'private_key',
    'pan',
    'cvc',
    'cvv',
    'ssn',
    'field_values',
    'credential_values',
    'credential_material',
    'plaintext',
    'encrypted_payload',
    'encrypted_data',
    ...IPG_FORBIDDEN_LOG_DATA_KEYS,
] as const;

export type ForbiddenLogDataKey = (typeof FORBIDDEN_LOG_DATA_KEYS)[number];

export const REDACTED_LOG_VALUE = '[REDACTED]' as const;

export type LogDataValue = JsonValue; //string | number | boolean | null | LogDataValue[] | LogDataUnsafe;

// Just used to ensure that LogDataValur is a subtype of JsonValue
export function _test(arg: LogDataValue): JsonValue {
    return arg;
}

export type LogDataUnsafe = {
    [key: string]: LogDataValue;
};

export type LogDataExcluded = {
    [K in ForbiddenLogDataKey]?: never;
};

export type LogData = LogDataUnsafe & LogDataExcluded;

export interface LogMeta {
    service: LogService;
    topics: LogTopic[];
    data?: LogData;
    exception?: LogException;
    subsystem?: string;
    workflow_id?: string;
    website_id?: string;
    correlation_id?: string;
    operation_id?: string;
    trace_id?: string;
}

export interface RequestLogEntry {
    // Timestamps
    client_timestamp?: string; // ISO-8601 (Device Time - Origin)

    // Context
    severity: LogSeverity;
    service: LogService;
    topics: LogTopic[];
    subsystem?: string; // e.g., 'samba-rpa', 'samba-sidebar', 'ipg', 'ipw'
    workflow_id?: string; // RPA workflow module identifier (if applicable)
    website_id?: string; // Website identifier for WebContentsView logs (if applicable)
    operation_id: string; // Client-generated hash
    correlation_id?: string; // Cross-system correlation for business workflows
    trace_id?: string; // GCP Trace ID (propagated via X-Cloud-Trace-Context)
    samba_image_tag?: SambaImageTag; // Samba build label injected by Samba logger

    // Customer ID is not inherent to the logged-in session and must be provided
    // by the caller in a context-dependent way.
    customer_id?: string;

    // Payload
    message: JsonValue; // Human readable
    args?: JsonArray; // Structured context (Redacted) - validated by explicit schema
    exception?: LogException;
    stack_trace?: string;
}
export interface AuthenticatedLogEntry extends RequestLogEntry, JsonObject {
    // Timestamps
    timestamp: string; // ISO-8601 (Server Time - Ingestion)

    // Identity (Injected by Backend for Client logs)
    user_id?: string;
    role_assignment_id?: string;
    audit_id?: string;
    organization_id?: string;

    // Customer ID is not inherent to the logged-in session and must be provided
    // by the caller in a context-dependent way.
    customer_id?: string;
}

export interface LogIngestRequest {
    entries: RequestLogEntry[];
}

export interface LogQueryRequest {
    user_id?: string;
    audit_id?: string;
    role_assignment_id?: string;
    organization_id?: string;
    customer_id?: string;
    trace_id?: string;
    from_timestamp?: string; // ISO-8601
    to_timestamp?: string; // ISO-8601
    limit?: number;
}

export interface LogQueryResponse {
    entries: AuthenticatedLogEntry[];
}

export interface GuidedCloudLoggingQueryRequest {
    mode: 'guided';
    start_timestamp: string;
    end_timestamp: string;
    text?: string;
    severities?: LogSeverity[];
    resource_types?: string[];
    log_names?: string[];
    trace_id?: string;
    limit?: number;
    cursor?: string;
}

export interface RawCloudLoggingQueryRequest {
    mode: 'raw';
    filter: string;
    limit?: number;
    cursor?: string;
}

// Discriminator: mode selects Guided compilation or Raw pass-through.
export type CloudLoggingQueryRequest = GuidedCloudLoggingQueryRequest | RawCloudLoggingQueryRequest;

export interface CloudLoggingEntry {
    timestamp?: string;
    severity?: LogSeverity;
    log_id?: string;
    resource_type?: string;
    trace?: string;
    insert_id?: string;
    details: JsonObject;
}

export interface ListCloudLoggingEntriesPageResponse {
    entries: CloudLoggingEntry[];
    pagination: OpaqueCursorPagination;
}

export interface CloudLoggingOptionSearchRequest {
    terms?: string[];
    limit?: number;
    cursor?: string;
}

export interface CloudLoggingOption {
    value: string;
    label: string;
}

export interface ListCloudLoggingOptionsPageResponse {
    options: CloudLoggingOption[];
    pagination: OpaqueCursorPagination;
}

export const CLOUD_LOGGING_DEFAULT_PAGE_LIMIT: number = 100;
export const CLOUD_LOGGING_MAX_PAGE_LIMIT: number = 100;
export const CLOUD_LOGGING_MAX_FILTER_LENGTH: number = 20_000;
export const CLOUD_LOGGING_MAX_CURSOR_LENGTH: number = 16_000;
export const CLOUD_LOGGING_MAX_OPTION_TERMS: number = 10;
export const CLOUD_LOGGING_MAX_OPTION_TERM_LENGTH: number = 100;

export const COMPLIANCE_LOG_TOPIC: LogTopic = 'COMPLIANCE';
